/**
 * Hostel mess menu — weekly editor + per-hostel save.
 *
 * Layout: a horizontal day selector (Mon–Sun) at the top, then a 4-row form
 * (Breakfast / Lunch / Snacks / Dinner) below. Loading the page for the first
 * time on a hostel auto-creates an empty 7×4 grid.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const MEALS = ["breakfast", "lunch", "snacks", "dinner"];

export default function MessMenu() {
  const [hostels, setHostels] = useState([]);
  const [hostelId, setHostelId] = useState("");
  const [menu, setMenu] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/hostels").then((r) => {
      setHostels(r.data || []);
      if (r.data?.[0]) setHostelId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!hostelId) return;
    api.get(`/mess/menus/${hostelId}`).then((r) => setMenu(r.data.menu || {}));
  }, [hostelId]);

  const updateCell = (day, meal, value) => {
    setMenu((m) => ({ ...m, [day]: { ...(m[day] || {}), [meal]: value } }));
  };

  const save = async () => {
    if (!hostelId) { toast.error("Pick a hostel first"); return; }
    setBusy(true);
    try {
      const payload = DAYS.reduce((acc, d) => ({ ...acc, [d]: menu[d] || {} }), {});
      await api.post(`/mess/menus/${hostelId}`, payload);
      toast.success("Weekly menu saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="vs-section">
        <h1 className="text-2xl font-semibold">Hostel mess menu</h1>
        <p className="text-sm text-[var(--vs-text-secondary)]">Weekly menu (Mon–Sun × Breakfast / Lunch / Snacks / Dinner). Parents see it inside their child's tab on the parent portal.</p>
      </div>

      <div className="flex items-end gap-3 mb-4">
        <div className="flex-1 max-w-xs">
          <Label>Hostel</Label>
          <Select value={hostelId} onValueChange={setHostelId}>
            <SelectTrigger data-testid="mess-hostel" className="bg-white"><SelectValue placeholder="Pick a hostel" /></SelectTrigger>
            <SelectContent>{hostels.map((h) => (<SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <Button data-testid="mess-save" onClick={save} disabled={busy || !hostelId}>{busy ? "Saving…" : "Save weekly menu"}</Button>
      </div>

      <div className="overflow-x-auto vs-card">
        <table className="w-full text-sm" data-testid="mess-table">
          <thead className="bg-[var(--vs-bg-subtle)]">
            <tr>
              <th className="text-left p-3 text-xs uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">Day</th>
              {MEALS.map((m) => (<th key={m} className="text-left p-3 text-xs uppercase font-mono tracking-widest text-[var(--vs-text-secondary)] capitalize">{m}</th>))}
            </tr>
          </thead>
          <tbody>
            {DAYS.map((d) => (
              <tr key={d} className="border-t border-[var(--vs-border)]/40" data-testid={`mess-row-${d}`}>
                <td className="p-2 capitalize font-medium">{d}</td>
                {MEALS.map((m) => (
                  <td key={m} className="p-1.5">
                    <Textarea
                      data-testid={`mess-${d}-${m}`}
                      rows={2}
                      value={(menu[d]?.[m]) || ""}
                      onChange={(e) => updateCell(d, m, e.target.value)}
                      placeholder={`${d.slice(0, 3)} ${m}`}
                      className="bg-white text-sm resize-y"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
